/**
 * API client wrapper. The API origin comes from `lib/api-base-url.ts` (`NEXT_PUBLIC_API_BASE_URL`, production fallback).
 * All responses follow standard envelope: { success, data?, error?, meta? }
 */
import type { ZodType } from "zod";
import { parseEnvelope } from "@/lib/api-envelope";
import { getApiBaseUrl } from "@/lib/api-base-url";
import { inSafeExamBrowser, sebProofHeaders } from "@/lib/seb-proof";

const getBaseUrl = () =>
  getApiBaseUrl();

// ── Module-level access token store ──────────────────────────────────────────
// The AuthProvider writes here on every token change so that fetcher() can
// inject Authorization: Bearer automatically without needing React context.
let _accessToken: string | null = null;

export function setFetcherToken(token: string | null): void {
  _accessToken = token;
}

export function getFetcherToken(): string | null {
  return _accessToken;
}

// ── Course preview token (X-Preview-Token) — memory + sessionStorage only ───
let _coursePreviewToken: string | null = null;

export function setCoursePreviewFetcherToken(token: string | null): void {
  _coursePreviewToken = token;
}

export function getCoursePreviewFetcherToken(): string | null {
  return _coursePreviewToken;
}

// ── Exam-scoped token (EX-EXM03) — separate slot, memory only ────────────────
// A roster student has no account, so AuthProvider never owns this token; it
// lives in its own slot (the course-preview pattern) and a real login always
// wins: the slot is only consulted when no access token is set.
let _examScopedToken: string | null = null;
/**
 * `HR-JOB01d`: a Hire candidate's exam token outranks a login. The candidate
 * sits as the roster seat's shadow user, so a Progsity account signed in on the
 * same browser (a student, a recruiter trying the link) must not be the one the
 * player's calls go out as; nor may a 401 silently refresh into that account.
 */
let _examScopedOutranksLogin = false;

export function setExamScopedFetcherToken(token: string | null, options?: { outranksLogin?: boolean }): void {
  _examScopedToken = token;
  _examScopedOutranksLogin = token !== null && options?.outranksLogin === true;
}

/** The bearer a request carries: a login's own token wins, unless an exam token was set to outrank it. */
function currentBearer(): string | null {
  if (_examScopedOutranksLogin) return _examScopedToken;
  return _accessToken ?? _examScopedToken;
}

export function getExamScopedFetcherToken(): string | null {
  return _examScopedToken;
}

// ── Module-level 401 refresh handler ─────────────────────────────────────────
// AuthProvider registers this so fetcher can silently refresh + retry on 401
// without needing React context. Returns the new access token or null on failure.
type RefreshFn = () => Promise<string | null>;
let _refreshFn: RefreshFn | null = null;
// Deduplicate concurrent 401s — all in-flight requests share one refresh call.
let _pendingRefresh: Promise<string | null> | null = null;

export function setRefreshFn(fn: RefreshFn | null): void {
  _refreshFn = fn;
}

/**
 * The silent refresh `fetcher` runs on a 401, shared with a caller that sends
 * its own request (`SUP-UI01`'s upload, which needs `XMLHttpRequest` for its
 * progress): one refresh at a time for the whole page. The new token, or
 * `null` when there is no session to refresh.
 */
export function refreshFetcherToken(): Promise<string | null> {
  if (!_refreshFn) return Promise.resolve(null);
  if (!_pendingRefresh) {
    _pendingRefresh = _refreshFn().finally(() => {
      _pendingRefresh = null;
    });
  }
  return _pendingRefresh;
}

/** Extra fields from PLAN_LIMIT_EXCEEDED (nested under `error.details` on the wire). */
export type QuizPlanGatePendingIntent = {
  id: string;
  intentType?: "subscription_upgrade" | "addon_purchase";
  plan?: string;
  addonType?: string;
  amount?: number;
  currency?: "BDT" | "USD";
  status?: "pending" | "awaiting_verification";
  expiresAt?: string;
  submittedAt?: string;
  verificationEstimate?: string;
};

export type QuizPlanLimitDetails = {
  upgradeRequired?: boolean;
  feature?: string;
  currentPlan?: string;
  requiredPlan?: string;
  currentUsage?: number;
  limit?: number;
  /**
   * `EX-GAT02`: the usage month a contests-per-month refusal counted over (ISO).
   * Absent on every other refusal.
   */
  periodStart?: string;
  resetsAt?: string;
  paymentPending?: boolean;
  pendingIntent?: QuizPlanGatePendingIntent;
  pendingIntentInsufficient?: boolean;
  pendingIntentHint?: string;
};

/** Typed API error that carries the HTTP status code and API error code. */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    public readonly planLimit?: QuizPlanLimitDetails,
    /** Raw `error.details` from the API envelope (RBAC, ownership, etc.). */
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export type ApiEnvelope<T = unknown> =
  | { success: true; data: T; meta?: { requestId?: string; timestamp?: string } }
  | { success: false; error: { code: string; message: string; details?: unknown }; meta?: Record<string, unknown> };

/** Read PLAN_LIMIT_EXCEEDED payload from `error.details` (or a raw object). */
export function parsePlanLimitDetails(raw: unknown): QuizPlanLimitDetails | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const d = raw as Record<string, unknown>;
  if (d["upgradeRequired"] !== true && d["paymentPending"] !== true) return undefined;
  const pendingIntent = parsePendingIntentDetails(d["pendingIntent"]);
  return {
    upgradeRequired: true,
    feature: typeof d["feature"] === "string" ? d["feature"] : undefined,
    currentPlan: typeof d["currentPlan"] === "string" ? d["currentPlan"] : undefined,
    requiredPlan: typeof d["requiredPlan"] === "string" ? d["requiredPlan"] : undefined,
    currentUsage: typeof d["currentUsage"] === "number" ? d["currentUsage"] : undefined,
    limit: typeof d["limit"] === "number" ? d["limit"] : undefined,
    ...(typeof d["periodStart"] === "string" ? { periodStart: d["periodStart"] } : {}),
    ...(typeof d["resetsAt"] === "string" ? { resetsAt: d["resetsAt"] } : {}),
    paymentPending: d["paymentPending"] === true,
    ...(pendingIntent ? { pendingIntent } : {}),
    pendingIntentInsufficient: d["pendingIntentInsufficient"] === true,
    pendingIntentHint: typeof d["pendingIntentHint"] === "string" ? d["pendingIntentHint"] : undefined,
  };
}

function parsePendingIntentDetails(raw: unknown): QuizPlanGatePendingIntent | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const d = raw as Record<string, unknown>;
  if (typeof d["id"] !== "string") return undefined;
  const currency = d["currency"] === "BDT" || d["currency"] === "USD" ? d["currency"] : undefined;
  const status =
    d["status"] === "pending" || d["status"] === "awaiting_verification"
      ? d["status"]
      : undefined;
  return {
    id: d["id"],
    intentType:
      d["intentType"] === "subscription_upgrade" || d["intentType"] === "addon_purchase"
        ? d["intentType"]
        : undefined,
    plan: typeof d["plan"] === "string" ? d["plan"] : undefined,
    addonType: typeof d["addonType"] === "string" ? d["addonType"] : undefined,
    amount: typeof d["amount"] === "number" ? d["amount"] : undefined,
    currency,
    status,
    expiresAt: typeof d["expiresAt"] === "string" ? d["expiresAt"] : undefined,
    submittedAt: typeof d["submittedAt"] === "string" ? d["submittedAt"] : undefined,
    verificationEstimate:
      typeof d["verificationEstimate"] === "string" ? d["verificationEstimate"] : undefined,
  };
}

/** Options that change *who* the request is, rather than what it asks for. */
export type FetcherIdentityOptions = {
  /**
   * Send the request as nobody: no `Authorization`, no preview or exam token,
   * no cookies, and no silent 401 refresh.
   *
   * `PG-EMBED01` needs this. The Progsity IDE embed runs inside a third
   * party's page, where a refresh loop against `api.progsity.io` has no
   * business running and third-party cookies would be partitioned away
   * anyway. An embed viewer is an anonymous viewer, which is also what puts
   * their runs on the 20/min IP ceiling rather than on somebody's plan.
   */
  anonymous?: boolean;
};

export async function fetcher<T = unknown>(
  path: string,
  opts?: RequestInit,
  dataSchema?: ZodType<T>,
  identity?: FetcherIdentityOptions
): Promise<ApiEnvelope<T>> {
  const anonymous = identity?.anonymous === true;
  const base = getBaseUrl().replace(/\/$/, "");
  const url = path.startsWith("/") ? `${base}${path}` : `${base}/${path}`;
  const headers = new Headers(opts?.headers);
  const method = (opts?.method ?? "GET").toUpperCase();
  const hasBody = opts?.body !== undefined && opts?.body !== null;
  const isFormData =
    typeof FormData !== "undefined" && opts?.body instanceof FormData;

  // Avoid forcing CORS preflights on simple GET/HEAD requests.
  if (
    hasBody &&
    !isFormData &&
    method !== "GET" &&
    method !== "HEAD" &&
    !headers.has("Content-Type")
  ) {
    headers.set("Content-Type", "application/json");
  }

  // Inject the access token from the module-level store if not already present.
  // The exam-scoped slot is a fallback only: a logged-in user's own token wins
  // (unless a Hire candidate's exam token was set to outrank it).
  const bearer = anonymous ? null : currentBearer();
  if (bearer && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${bearer}`);
  }

  if (!anonymous && _coursePreviewToken && !headers.has("X-Preview-Token")) {
    headers.set("X-Preview-Token", _coursePreviewToken);
  }

  // `EX-SEC12`: inside Safe Exam Browser, the page's proof of the exam's file.
  // Outside SEB nothing more is sent and nothing is awaited, so an ordinary
  // browser's request leaves exactly when it did before.
  const sebProof = anonymous || !inSafeExamBrowser() ? null : await sebProofHeaders();
  if (sebProof) for (const [name, value] of Object.entries(sebProof)) headers.set(name, value);

  let res = await fetch(url, {
    ...opts,
    credentials: anonymous ? "omit" : "include",
    headers,
  });

  // 401 interceptor: silently refresh and retry once.
  // Skip auth endpoints to prevent recursive refresh loops.
  if (!anonymous && !_examScopedOutranksLogin && res.status === 401 && _refreshFn && !path.startsWith("/v1/auth/")) {
    const newToken = await refreshFetcherToken();
    if (newToken) {
      const retryHeaders = new Headers(opts?.headers);
      if (
        hasBody &&
        !isFormData &&
        method !== "GET" &&
        method !== "HEAD" &&
        !retryHeaders.has("Content-Type")
      ) {
        retryHeaders.set("Content-Type", "application/json");
      }
      retryHeaders.set("Authorization", `Bearer ${newToken}`);
      if (_coursePreviewToken && !retryHeaders.has("X-Preview-Token")) {
        retryHeaders.set("X-Preview-Token", _coursePreviewToken);
      }
      if (sebProof) for (const [name, value] of Object.entries(sebProof)) retryHeaders.set(name, value);
      res = await fetch(url, { ...opts, credentials: "include", headers: retryHeaders });
    }
  }

  const raw = await res.json();
  // `SITE-SPD04`: read without zod, so zod is not in every page's script.
  const parsed = parseEnvelope(raw);

  if (!parsed.success) {
    const message = parsed.error?.message ?? `Request failed: ${res.status}`;
    const planLimit = parsePlanLimitDetails(parsed.error?.details);
    throw new ApiError(message, res.status, parsed.error?.code, planLimit, parsed.error?.details);
  }

  if (!res.ok) {
    throw new ApiError(
      parsed.error?.message ?? `Request failed: ${res.status}`,
      res.status
    );
  }

  const data = dataSchema ? dataSchema.parse(parsed.data) : (parsed.data as T);
  return {
    success: true,
    data,
    meta: parsed.meta,
  };
}

/** What `downloadAuthedFile` did with the answer: saved the file, or found it not ready (a 202). */
export type AuthedDownloadResult = "saved" | "pending";

/**
 * Downloads an authenticated file and hands it to the browser — `EX-EXM06`.
 *
 * `fetcher` cannot serve this: an export is CSV or PDF, not an envelope, so the
 * body has to come back as a blob. What it *must* still do is carry the same
 * credential, and that is why this exists rather than a bare `fetch`.
 *
 * **The defect this replaced:** the quiz export used `fetch(url, { credentials:
 * "include" })` and no `Authorization` header. There is no cookie-based access
 * token on this platform — `authenticate` reads `Authorization: Bearer` and
 * nothing else — so that request was answered `401` every single time and the
 * export button had never worked. A download that goes around the client also
 * goes around the token store; going through here is what stops that recurring.
 *
 * Refresh-on-401 is deliberately **not** replicated: `fetcher` owns that retry and
 * duplicating it here would give one page two independent refresh loops. A user
 * whose token expired mid-session gets a clear failure and a working retry on the
 * next click, because any other request on the page refreshes the shared token.
 *
 * A `202` answers `"pending"` and saves nothing: it is a file route saying the
 * file is not stored yet (a certificate PDF still being generated), and its body
 * is the envelope, so saving it would hand the reader a JSON file named `.pdf`.
 */
export async function downloadAuthedFile(
  path: string,
  fallbackFilename: string,
): Promise<AuthedDownloadResult> {
  const res = await requestAuthedFile(path);
  if (res.status === 202) return "pending";
  saveBlobAs(await res.blob(), filenameOf(res, fallbackFilename));
  return "saved";
}

/** A file read with the bearer, kept in memory: its bytes and the name the server gave it. */
export type AuthedFile = { blob: Blob; filename: string };

/**
 * `EX-PDF02`: {@link downloadAuthedFile}'s request, handed back rather than
 * saved, for a caller that does something else with the file (opens it in a
 * tab to print). A refusal throws the same `ApiError`, details included, so a
 * busy printer's `retryAfterSeconds` reaches the caller.
 *
 * `EX-PDF03`: `json` makes it a `POST` of that body, for a file route that
 * takes one (the author set, `POST /v1/judge-platform/problem-sets/pdf`).
 */
export async function fetchAuthedFile(
  path: string,
  fallbackFilename: string,
  json?: unknown,
): Promise<AuthedFile> {
  const res = await requestAuthedFile(path, json);
  return { blob: await res.blob(), filename: filenameOf(res, fallbackFilename) };
}

/**
 * `SUP-UI01`: a file route that answers a short signed read under
 * `Accept: application/json` when its store can sign (`docs/28` §5.2), and the
 * bytes otherwise (local storage). Either way the caller gets something to
 * show: the signed address, which an `<img>` or a download can use until it
 * expires, or the file itself.
 */
export async function fetchAuthedFileOrUrl(
  path: string,
  fallbackFilename: string,
): Promise<{ url: string } | AuthedFile> {
  const res = await requestAuthedFile(path, undefined, "application/json");
  if (!(res.headers.get("Content-Type") ?? "").includes("application/json")) {
    return { blob: await res.blob(), filename: filenameOf(res, fallbackFilename) };
  }
  const parsed = parseEnvelope(await res.json());
  const url = parsed.success ? (parsed.data as { url?: unknown } | undefined)?.url : undefined;
  if (typeof url !== "string") throw new ApiError(`Download failed (${res.status})`, res.status);
  return { url };
}

/** Hands a blob to the browser as a download under `filename`. */
export function saveBlobAs(blob: Blob, filename: string): void {
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(href);
}

function filenameOf(res: Response, fallbackFilename: string): string {
  const disposition = res.headers.get("Content-Disposition");
  const match = disposition?.match(/filename="?([^";]+)"?/i);
  return match?.[1] ?? fallbackFilename;
}

async function requestAuthedFile(path: string, json?: unknown, accept?: string): Promise<Response> {
  const base = getBaseUrl().replace(/\/$/, "");
  const url = path.startsWith("/") ? `${base}${path}` : `${base}/${path}`;
  const headers = new Headers();
  const bearer = currentBearer();
  if (bearer) headers.set("Authorization", `Bearer ${bearer}`);

  if (json !== undefined) headers.set("Content-Type", "application/json");
  if (accept) headers.set("Accept", accept);

  const res = await fetch(url, {
    method: json === undefined ? "GET" : "POST",
    credentials: "include",
    headers,
    ...(json === undefined ? {} : { body: JSON.stringify(json) }),
  });

  if (!res.ok) {
    // An error on a file route still comes back as the standard envelope, so the
    // plan-gate and RBAC details a caller wants to act on survive.
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      throw new ApiError(`Download failed (${res.status})`, res.status);
    }
    const err = body as { error?: { code?: string; message?: string; details?: unknown } };
    throw new ApiError(
      err.error?.message ?? `Download failed (${res.status})`,
      res.status,
      err.error?.code,
      parsePlanLimitDetails(err.error?.details),
      err.error?.details,
    );
  }

  return res;
}
